import { createContext, useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "./UserContext";
import Dialog from "@/components/Dialog";
import SadFaceIcon from "/assets/icons/sad-face.svg";
import { noAuthRequired } from "./ProtectRoutesContext";
import { useBlockingDeadlineSnapshot } from "@/hooks/useBlockingDeadlineSnapshot";
import { invalidateRegistrationProgressQueries } from "@/services/inscription/invalidateRegistrationProgressQueries";
import { syncPdcCourseProgressToRealityThenRefreshQueries } from "@/services/courses";
import { ParticipantUiPhase } from "@/services/inscription/types";
import type { Course } from "@/services/courses/types/course.types";
import { shouldShowPdcLagCelebrationModal } from "@/utils/inscriptionPdcMerge";
import {
  canFetchPdcExternalCourseList,
  isPdcExternalCourseListEnabled,
} from "@/utils/pdc-external-course-list";
import { useQuery } from "react-query";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useStages } from "./StagesContext";
import {
  isPastFirstRealityStage,
  isPostAdvanceProfileComplete,
  needsCompleteRegistration,
} from "@/utils/registrationProfile";
import {
  isRegistrationCompleteForBlocking,
  resolveParticipantBlockingDialogText,
  shouldBlockIncompleteRegistration,
  shouldBypassInscriptionDeadlineBlocking,
} from "@/utils/participantBlockingMessage";
import {
  clearPostAdvanceRegionalBriefingPending,
  clearPostAdvanceWelcomeDismissed,
  isPostAdvanceWelcomeDismissed,
  markPostAdvanceWelcomeDismissed,
  shouldShowPostAdvanceRegionalBriefing,
} from "@/utils/postAdvanceRegionalBriefing";
import { Box } from "@mui/material";
import Text from "@/components/Text";

type SessionDialogsContext = {};

const SessionDialogsContext = createContext<SessionDialogsContext>(
  {} as SessionDialogsContext
);

type SessionDialogsProps = {
  children: React.ReactNode;
};

function PostAdvanceWelcomeDialogContent() {
  return (
    <Box
      sx={{
        mt: "1rem",
        mb: "1.5rem",
        px: "1rem",
        width: "100%",
      }}
    >
      <Text
        display="block"
        align="center"
        fontSize="1rem"
        fontWeight={700}
        lineHeight="1.2rem"
        mb="1rem"
      >
        Parabéns! Você avançou de fase.
      </Text>
      <Text
        display="block"
        align="center"
        fontSize="1rem"
        lineHeight="1.2rem"
        mb="1rem"
      >
        Para confirmar seu avanço de etapa e receber a primeira premiação,
        complete seus dados.
      </Text>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "0.35rem",
          mb: "0.5rem",
        }}
      >
        <span style={{ fontSize: "1rem", lineHeight: 1 }} role="img" aria-hidden>
          ⚠️
        </span>
        <Text
          component="span"
          display="inline"
          fontSize="1rem"
          fontWeight={700}
          lineHeight="1.2rem"
        >
          Atenção:
        </Text>
      </Box>
      <Text display="block" align="center" fontSize="1rem" lineHeight="1.2rem">
        a Quartzolit não entra em contato solicitando PIX ou informações
        adicionais. Caso receba qualquer comunicação suspeita, entre em contato
        com nossos canais oficiais.
      </Text>
    </Box>
  );
}

function PostAdvanceRegionalBriefingDialogContent() {
  return (
    <Box
      sx={{
        mt: "1rem",
        mb: "1.5rem",
        px: "1rem",
        width: "100%",
      }}
    >
      <Text
        display="block"
        align="center"
        fontSize="1rem"
        lineHeight="1.2rem"
        sx={{ textAlign: "center", width: "100%" }}
      >
        Nossa equipe entrará em contato com você para enviar as orientações sobre{" "}
        <Text
          component="span"
          display="inline"
          fontSize="1rem"
          fontWeight={700}
          lineHeight="1.2rem"
        >
          transporte e hospedagem para
        </Text>{" "}
        a participação da etapa.
      </Text>
    </Box>
  );
}

type BlockingModal =
  | null
  | "celebration"
  | "waiting"
  | "notClassified";

export const SessionDialogsProvider = ({ children }: SessionDialogsProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo, logOut } = useUser();
  const {
    isLoading,
    isUserOutOfReality,
    isSemiFinalist,
    currentStageNumber,
    currentStage,
    stages,
  } = useStages();

  const isAuthenticatedPage = useMemo(() => {
    return !noAuthRequired.includes(location.pathname);
  }, [location.pathname]);

  const {
    realityInfo,
    inscription,
    isLoading: isDeadlineSnapshotLoading,
    refresh: refreshDeadlineSnapshot,
  } = useBlockingDeadlineSnapshot({
    userId: userInfo?.id,
    enabled: isAuthenticatedPage && !!userInfo?.id,
    routeKey: location.pathname,
  });

  const isInscriptionLoading = isDeadlineSnapshotLoading;

  const usePdcList = isPdcExternalCourseListEnabled();
  /** Reads `get-courses-pdc` cache only (fetch runs on Dashboard/Courses to avoid duplicate GET /external/courses). */
  const { data: pdcCourses } = useQuery<Course[]>(
    ["get-courses-pdc", userInfo?.id],
    async () => [],
    { enabled: false },
  );

  const userId = useMemo(() => userInfo?.id.toString(), [userInfo?.id]);

  const lastProgressRefetchAt = useRef(0);
  const prevPathnameRef = useRef<string | null>(null);

  /** Courses finish on external LMS; when the tab becomes visible again, refetch inscription/progress. */
  useEffect(() => {
    if (!userInfo?.id || !isAuthenticatedPage) return;

    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastProgressRefetchAt.current < 2500) return;
      lastProgressRefetchAt.current = now;
      void (async () => {
        if (
          isPdcExternalCourseListEnabled() &&
          canFetchPdcExternalCourseList(userInfo)
        ) {
          await syncPdcCourseProgressToRealityThenRefreshQueries();
        } else {
          await invalidateRegistrationProgressQueries();
        }
        await refreshDeadlineSnapshot();
      })();
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () =>
      document.removeEventListener("visibilitychange", onVisibility);
  }, [userInfo, isAuthenticatedPage, refreshDeadlineSnapshot]);

  const isRegistrationComplete = useMemo(
    () =>
      isRegistrationCompleteForBlocking(
        inscription,
        pdcCourses,
        usePdcList
      ),
    [inscription, pdcCourses, usePdcList]
  );

  /** One blocking modal from inscription / legacy stage rules (priority over advanced-stage dialogs). */
  const blockingModal: BlockingModal = useMemo(() => {
    if (isInscriptionLoading || !inscription) {
      return null;
    }

    if (
      shouldBypassInscriptionDeadlineBlocking(inscription, currentStageNumber, {
        currentStageId: currentStage?.id,
        stages,
      })
    ) {
      if (inscription.participantUiPhase === ParticipantUiPhase.NOT_CLASSIFIED) {
        return "notClassified";
      }
      return null;
    }

    const phase = inscription.participantUiPhase;
    if (phase === ParticipantUiPhase.INSCRIPTION_COMPLETED_CELEBRATION) {
      return "celebration";
    }
    if (
      shouldShowPdcLagCelebrationModal(
        inscription,
        pdcCourses,
        usePdcList,
        isUserOutOfReality
      )
    ) {
      return "celebration";
    }
    if (phase === ParticipantUiPhase.WAITING_FINALISTS_ANNOUNCEMENT) {
      return "waiting";
    }
    if (phase === ParticipantUiPhase.NOT_CLASSIFIED) {
      return "notClassified";
    }
    if (
      shouldBlockIncompleteRegistration(
        inscription,
        realityInfo,
        isRegistrationComplete
      )
    ) {
      return "celebration";
    }
    if (
      isUserOutOfReality === true &&
      phase === ParticipantUiPhase.NONE &&
      !inscription.advancedToResultStage
    ) {
      return "notClassified";
    }
    return null;
  }, [
    inscription,
    isInscriptionLoading,
    currentStageNumber,
    currentStage?.id,
    stages,
    isUserOutOfReality,
    usePdcList,
    pdcCourses,
    realityInfo,
    isRegistrationComplete,
  ]);

  const participantBlockingDialogText = useMemo(
    () =>
      resolveParticipantBlockingDialogText({
        realityInfo,
        isRegistrationComplete,
        participantUiPhase: inscription?.participantUiPhase,
      }),
    [
      realityInfo,
      isRegistrationComplete,
      inscription?.participantUiPhase,
    ]
  );

  const isNotClassifiedFromApi = useMemo(
    () =>
      inscription?.participantUiPhase === ParticipantUiPhase.NOT_CLASSIFIED,
    [inscription?.participantUiPhase]
  );

  const inscriptionBlocksUi = blockingModal !== null;

  const advancedToResultStage = inscription?.advancedToResultStage === true;

  const hasPostAdvanceDataFilled = useMemo(
    () =>
      isPostAdvanceProfileComplete(
        userInfo,
        currentStageNumber,
        advancedToResultStage
      ),
    [userInfo, currentStageNumber, advancedToResultStage]
  );

  const [welcomeDismissVersion, setWelcomeDismissVersion] = useState(0);

  useEffect(() => {
    if (!userId) {
      clearPostAdvanceWelcomeDismissed();
    }
  }, [userId]);

  const isWelcomeModalDismissed = useMemo(() => {
    if (!userId) return false;
    return isPostAdvanceWelcomeDismissed(userId);
    // welcomeDismissVersion: re-read sessionStorage after dismiss in same session
  }, [userId, welcomeDismissVersion]);

  /** Post-advance profile still incomplete. */
  const needsPostAdvanceCompletion = useMemo(() => {
    if (!userId || !isAuthenticatedPage) return false;
    if (isInscriptionLoading || !inscription) return false;
    if (inscriptionBlocksUi) return false;
    if (isUserOutOfReality === true && !advancedToResultStage) return false;
    if (!isPastFirstRealityStage(currentStageNumber, advancedToResultStage)) {
      return false;
    }
    if (
      isLoading &&
      !advancedToResultStage &&
      currentStageNumber <= 1
    ) {
      return false;
    }
    return !hasPostAdvanceDataFilled;
  }, [
    userId,
    isAuthenticatedPage,
    isInscriptionLoading,
    inscription,
    isUserOutOfReality,
    inscriptionBlocksUi,
    currentStageNumber,
    advancedToResultStage,
    isLoading,
    hasPostAdvanceDataFilled,
  ]);

  /**
   * 2ª parte: briefing regional após salvar cadastro complementar completo.
   */
  const isPostAdvanceRegionalBriefingOpen = useMemo(
    () =>
      shouldShowPostAdvanceRegionalBriefing({
        userId,
        isStagesLoading: isLoading,
        isAuthenticatedPage,
        isInscriptionLoading,
        hasInscription: !!inscription,
        isUserOutOfReality,
        inscriptionBlocksUi,
        currentStageNumber,
        advancedToResultStage,
        hasPostAdvanceProfileComplete: hasPostAdvanceDataFilled,
      }),
    [
      userId,
      isLoading,
      isAuthenticatedPage,
      isInscriptionLoading,
      inscription,
      isUserOutOfReality,
      inscriptionBlocksUi,
      currentStageNumber,
      advancedToResultStage,
      hasPostAdvanceDataFilled,
    ]
  );

  /**
   * 1ª parte: campos complementares incompletos — reaparece a cada login e em /profile.
   * Cadastro básico (CPF/foto) tem prioridade via modal de inscrição.
   */
  const isPostAdvanceWelcomeOpen = useMemo(() => {
    if (isInscriptionLoading || !inscription) return false;
    if (isPostAdvanceRegionalBriefingOpen) return false;
    if (!needsPostAdvanceCompletion) return false;
    if (isWelcomeModalDismissed) return false;
    if (needsCompleteRegistration(userInfo)) return false;
    return true;
  }, [
    isInscriptionLoading,
    inscription,
    isPostAdvanceRegionalBriefingOpen,
    needsPostAdvanceCompletion,
    isWelcomeModalDismissed,
    userInfo,
  ]);

  const frozenBriefingPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (isPostAdvanceRegionalBriefingOpen) {
      if (!frozenBriefingPathRef.current) {
        frozenBriefingPathRef.current = location.pathname;
      }
      return;
    }
    frozenBriefingPathRef.current = null;
  }, [isPostAdvanceRegionalBriefingOpen, location.pathname]);

  /**
   * On /profile without post-advance data: block sidebar navigation until saved.
   * Welcome modal may overlay /profile until the participant continues to the form.
   */
  useEffect(() => {
    if (prevPathnameRef.current === null) {
      prevPathnameRef.current = location.pathname;
      return;
    }
    const previous = prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;

    if (!needsPostAdvanceCompletion) return;
    if (needsCompleteRegistration(userInfo)) return;
    if (previous === "/profile" && location.pathname !== "/profile") {
      navigate("/profile", { replace: true });
    }
  }, [
    location.pathname,
    needsPostAdvanceCompletion,
    userInfo,
    navigate,
  ]);

  /** Pós-cadastro completo: bloqueia navegação em qualquer rota até clicar em Sair. */
  useEffect(() => {
    if (!isPostAdvanceRegionalBriefingOpen) return;
    const frozen = frozenBriefingPathRef.current;
    if (!frozen || location.pathname === frozen) return;
    navigate(frozen, { replace: true });
  }, [
    isPostAdvanceRegionalBriefingOpen,
    location.pathname,
    navigate,
  ]);

  const logoutAndClose = () => {
    clearPostAdvanceRegionalBriefingPending();
    clearPostAdvanceWelcomeDismissed();
    void logOut();
  };

  return (
    <SessionDialogsContext.Provider value={{}}>
      {children}
      <Dialog
        isOpen={blockingModal === "notClassified"}
        icon={<img src={SadFaceIcon} alt="" />}
        text="Parabéns pela sua participação! Desta vez você não avançou para a próxima etapa do reality. Continue com a gente nas próximas edições."
        confirmButtonText={
          isNotClassifiedFromApi || !isSemiFinalist ? "Sair" : "Entendi"
        }
        onConfirm={async () => {
          if (isNotClassifiedFromApi || !isSemiFinalist) {
            await logOut();
          }
        }}
        onDismiss={async () => {
          if (isNotClassifiedFromApi || !isSemiFinalist) {
            await logOut();
          }
        }}
      />

      <Dialog
        isOpen={blockingModal === "celebration"}
        text={participantBlockingDialogText}
        confirmButtonText="Sair"
        onConfirm={logoutAndClose}
        onDismiss={logoutAndClose}
      />

      <Dialog
        isOpen={blockingModal === "waiting"}
        text={participantBlockingDialogText}
        confirmButtonText="Sair"
        onConfirm={logoutAndClose}
        onDismiss={logoutAndClose}
      />

      <Dialog
        isOpen={isPostAdvanceWelcomeOpen}
        preventBackdropClose
        maxWidth="24rem"
        icon={
          <span
            style={{ fontSize: "2.75rem", lineHeight: 1 }}
            role="img"
            aria-hidden
          >
            🎉
          </span>
        }
        content={<PostAdvanceWelcomeDialogContent />}
        confirmButtonText="Completar cadastro"
        onConfirm={() => {
          if (userId) {
            markPostAdvanceWelcomeDismissed(userId);
            setWelcomeDismissVersion((version) => version + 1);
          }
          if (location.pathname !== "/profile") {
            navigate("/profile");
          }
        }}
      />

      <Dialog
        isOpen={isPostAdvanceRegionalBriefingOpen}
        preventBackdropClose
        maxWidth="26rem"
        icon={
          <span
            style={{ fontSize: "2.25rem", lineHeight: 1 }}
            role="img"
            aria-hidden
          >
            📅
          </span>
        }
        content={<PostAdvanceRegionalBriefingDialogContent />}
        confirmButtonText="Sair"
        onConfirm={logoutAndClose}
      />
    </SessionDialogsContext.Provider>
  );
};
